import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/marketing/ProductCard";
import { trackViewItemList } from "@/lib/analytics";
import SizeFinder from "@/components/shop/SizeFinder";
import Offers from "@/components/shop/Offers";

const LIST_NAME = "Shop";
const SECTIONS = [
  { kind: "bottle", title: "Jars", sub: "One oil, from the 100 ml Chintu Edition for your hair routine to the 1 litre kitchen jar. New to it? Start small." },
  { kind: "bundle", title: "Family packs", sub: "Several litres in one delivery, for kitchens that cook with it every day." },
  { kind: "gift", title: "Gift packs", sub: "Boxed, with a card for your note." },
];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts({})
      .then((d) => {
        const list = d.products || [];
        setProducts(list);
        trackViewItemList(LIST_NAME, list);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Seo
        title="Buy Cold Pressed Coconut Oil Online"
        description="Buy Karijeeva cold pressed coconut oil online: one ingredient, grown on our family farms in Karnataka, every batch lab tested. Delivered across India."
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Shop", path: "/products" },
        ])}
      />

      <section className="bg-brand-parchment">
        <div className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-10 text-center">
          <h1 className="font-display text-[clamp(2.6rem,7vw,5rem)] leading-[1] tracking-display text-brand-ink" data-testid="products-heading">
            Shop Karijeeva.
          </h1>
          <p className="mt-5 max-w-2xl mx-auto font-body text-[18px] sm:text-[20px] text-ink-muted leading-snug">
            One oil, pressed cold from our own coconuts. Pick the size that suits your kitchen.
          </p>
          <div className="mt-8 max-w-xl mx-auto text-left"><Offers /></div>
        </div>
      </section>

      <section className="bg-brand-parchment pb-24">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <p className="font-body text-ink-muted text-center">Loading</p>
          ) : products.length === 0 ? (
            <p className="font-body text-ink-muted text-center">This batch has sold out. Join our list in the footer and we'll write when the next one is ready.</p>
          ) : (
            <div className="space-y-16" data-testid="products-grid">
              {SECTIONS.map((sec) => {
                const list = products.filter((p) => (p.kind || "bottle") === sec.kind);
                if (!list.length) return null;
                return (
                  <div key={sec.kind} data-testid={`shop-section-${sec.kind}`}>
                    <div className="mb-6">
                      <h2 className="font-display text-[28px] tracking-heading text-brand-ink">{sec.title}</h2>
                      <p className="mt-1 font-body text-[15px] text-ink-muted">{sec.sub}</p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {list.map((p) => (
                        <ProductCard key={p.slug} product={p} index={products.indexOf(p)} listName={LIST_NAME} />
                      ))}
                    </div>
                  </div>
                );
              })}
              <Link to="/monthly-refill" className="block rounded-3xl bg-brand-obsidian text-white p-8 sm:p-10" data-testid="shop-refill-band">
                <p className="font-body text-[14px] font-semibold text-brand-bone/70">Monthly refill</p>
                <p className="mt-2 font-display text-[26px] tracking-heading">Never run out, never get charged by surprise.</p>
                <p className="mt-2 font-body text-[15px] text-brand-bone/75">Tick Remind me to refill at checkout. We message you with a one tap reorder link. No auto debit.</p>
              </Link>
            </div>
          )}
        </div>
      </section>

      <SizeFinder title="Not sure which size? Two taps." />

      <section className="bg-brand-parchment-soft py-20">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-3 gap-4">
          {[
            ["Not sure it's pure?", "Read how we test every batch, and how you can check any coconut oil at home.", "/how-to-check-coconut-oil-purity", "Check purity"],
            ["Why cold pressed?", "What refining does to coconut oil, and when each kind makes sense.", "/cold-pressed-vs-refined-coconut-oil", "Compare"],
            ["Where it grows", "Our farms in Tiptur and on the coast from Gokarna to Shirali.", "/the-farm", "Visit our farms"],
          ].map(([t, b, to, cta]) => (
            <Link key={to} to={to} className="group rounded-3xl bg-white p-8 block">
              <p className="font-display text-[22px] tracking-[-0.02em] text-brand-ink">{t}</p>
              <p className="mt-2 font-body text-[15px] text-ink-muted leading-relaxed">{b}</p>
              <p className="mt-5 font-body text-[15px] text-brand-gold group-hover:underline">{cta}</p>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
